import React, { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Phone, Mail } from "lucide-react";
import { EntityPage } from "@/components/EntityPage";
import DetailPopup from "@/components/DetailPopup";
import WhatsAppShortcuts from "@/components/WhatsAppShortcuts";
import WhatsAppButton from "@/components/WhatsAppButton";
import AdminDeleteButton from "@/components/AdminDeleteButton";

export function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", address: "", notes: "" });

  const load = async () => setItems((await api.get("/customers")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm({ name: "", phone: "", email: "", company: "", address: "", notes: "" });
    setShowAdd(true);
  };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name || "", phone: c.phone || "", email: c.email || "", company: c.company || "", address: c.address || "", notes: c.notes || "" });
    setShowAdd(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/customers/${editingId}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer added");
      }
      setForm({ name: "", phone: "", email: "", company: "", address: "", notes: "" });
      setEditingId(null);
      setShowAdd(false);
      load();
    } catch(err){ toast.error(err.response?.data?.detail || "Failed"); }
  };

  const shown = q ? items.filter(c => [c.name,c.phone,c.email,c.company].join(" ").toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Directory</div>
          <h1 className="font-display text-3xl tracking-tight">Customers</h1>
          <p className="text-sm text-[#757169] mt-1">Click any customer for their full history. Duplicate phone/email blocked.</p>
        </div>
        <div className="flex items-center gap-2">
          <input className="input-field w-56" placeholder="Search name/phone/email…" value={q} onChange={e=>setQ(e.target.value)} data-testid="customer-search"/>
          <button className="btn-primary flex items-center gap-2" onClick={()=>{ showAdd ? setShowAdd(false) : openNew(); }} data-testid="customers-add-btn">
            {showAdd ? <><X size={16}/> Cancel</> : <><Plus size={16}/> New customer</>}
          </button>
        </div>
      </div>
      {showAdd && (
        <form onSubmit={submit} className="card-surface p-5 grid grid-cols-1 md:grid-cols-2 gap-4 fade-up" data-testid="customer-form">
          <div className="md:col-span-2 label-tiny text-[#C45A41]">{editingId ? "Editing existing customer" : "New customer"}</div>
          <div><div className="label-tiny mb-1">Name *</div><input required className="input-field" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="customer-form-name"/></div>
          <div><div className="label-tiny mb-1">Company</div><input className="input-field" value={form.company} onChange={e=>setForm({...form,company:e.target.value})}/></div>
          <div><div className="label-tiny mb-1">Phone <span className="text-[10px] normal-case">(must be unique)</span></div><input className="input-field" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/></div>
          <div><div className="label-tiny mb-1">Email <span className="text-[10px] normal-case">(must be unique)</span></div><input className="input-field" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Address</div><input className="input-field" value={form.address} onChange={e=>setForm({...form,address:e.target.value})}/></div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Notes</div><textarea rows={2} className="input-field" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></div>
          <div className="md:col-span-2 flex justify-end"><button className="btn-primary" data-testid="customer-form-submit">{editingId ? "Save changes" : "Save"}</button></div>
        </form>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.length === 0 && <div className="text-sm text-[#757169] col-span-full text-center p-8">No customers found.</div>}
        {shown.map(c => (
          <div key={c.id} className="card-surface p-4 hover:shadow-md transition-shadow fade-up flex flex-col gap-2" data-testid={`customer-card-${c.id}`}>
            <Link to={`/customers/${c.id}`} className="min-w-0">
              <div className="font-display font-semibold text-lg truncate">{c.name}</div>
              {c.company && <div className="text-sm text-[#757169] truncate">{c.company}</div>}
              <div className="mt-2 space-y-1 text-xs text-[#757169]">
                {c.phone && <div className="flex items-center gap-1.5"><Phone size={11}/> <span className="mono">{c.phone}</span></div>}
                {c.email && <div className="flex items-center gap-1.5 truncate"><Mail size={11}/> {c.email}</div>}
              </div>
            </Link>
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <button type="button" onClick={()=>openEdit(c)} className="btn-ghost text-[11px]" data-testid={`edit-customer-${c.id}`}>Edit</button>
              <WhatsAppShortcuts phone={c.phone} name={c.name} variant="compact" testid={`wa-customer-${c.id}`} />
              <AdminDeleteButton endpoint={`/customers/${c.id}`} label={`customer "${c.name}"`} onDone={load} testid={`delete-customer-${c.id}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// The other simple entities we keep as before

export function Vendors() {
  return <VendorsWithPayables />;
}

function VendorsWithPayables() {
  const [vendors, setVendors] = React.useState([]);
  const [payables, setPayables] = React.useState([]);
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({ name: "", contact: "", phone: "", email: "", address: "", map_link: "", material_types: "" });

  const load = async () => {
    setVendors((await api.get("/vendors")).data);
    setPayables((await api.get("/vendors-payables-summary")).data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Name & phone required");
    try {
      await api.post("/vendors", form);
      toast.success("Vendor added");
      setForm({ name: "", contact: "", phone: "", email: "", address: "", map_link: "", material_types: "" });
      setShowAdd(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const payableById = Object.fromEntries(payables.map(p => [p.vendor_id, p]));
  const totalBilled = payables.reduce((a, b) => a + Number(b.billed || 0), 0);
  const totalPaid = payables.reduce((a, b) => a + Number(b.paid || 0), 0);
  const totalOutstanding = payables.reduce((a, b) => a + Number(b.outstanding || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Directory</div>
          <h1 className="font-display text-3xl tracking-tight">Vendors</h1>
          <p className="text-sm text-[#757169] mt-1">Procurement partners + customization units. Click any row for full ledger, POs, jobs & damage trend.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowAdd(v => !v)} data-testid="vendors-add-btn">
          {showAdd ? <><X size={16}/> Cancel</> : <><Plus size={16}/> New vendor</>}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submit} className="card-surface p-5 grid grid-cols-1 md:grid-cols-2 gap-4 fade-up" data-testid="vendors-form">
          <div><div className="label-tiny mb-1">Name *</div><input required className="input-field" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
          <div><div className="label-tiny mb-1">Contact person</div><input className="input-field" value={form.contact} onChange={e=>setForm({...form,contact:e.target.value})}/></div>
          <div><div className="label-tiny mb-1">Phone * <span className="text-[10px] normal-case">(must be unique)</span></div><input required className="input-field" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/></div>
          <div><div className="label-tiny mb-1">Email (for PO)</div><input className="input-field" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Address</div><input className="input-field" value={form.address} onChange={e=>setForm({...form,address:e.target.value})}/></div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Map link (Google Maps)</div><input className="input-field" value={form.map_link} onChange={e=>setForm({...form,map_link:e.target.value})}/></div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Material / service</div><input className="input-field" value={form.material_types} onChange={e=>setForm({...form,material_types:e.target.value})}/></div>
          <div className="md:col-span-2 flex justify-end"><button className="btn-primary" data-testid="vendors-submit">Save vendor</button></div>
        </form>
      )}

      {/* Ledger KPIs */}
      <div className="card-surface p-5" data-testid="vendors-ledger-summary">
        <div className="label-tiny mb-3">Payables ledger — across all vendors</div>
        <div className="grid grid-cols-3 gap-3">
          <VLKpi label="Total billed" value={inr(totalBilled)} tone="text-[#2C2B29]"/>
          <VLKpi label="Paid so far" value={inr(totalPaid)} tone="text-[#4A7258]"/>
          <VLKpi label="Outstanding" value={inr(totalOutstanding)} tone={totalOutstanding > 0 ? "text-[#B83A3A]" : "text-[#4A7258]"}/>
        </div>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>Vendor</th><th>Contact</th><th>Phone</th><th>Handles</th><th>Billed</th><th>Paid</th><th>Outstanding</th><th>Actions</th></tr></thead>
          <tbody>
            {vendors.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#757169]">No vendors yet.</td></tr>}
            {vendors.map(v => {
              const p = payableById[v.id] || { billed: 0, paid: 0, outstanding: 0 };
              const remindMsg = `Hi ${v.name}, this is Marsh Clothing. Our books show an outstanding balance of ₹${Number(p.outstanding).toLocaleString("en-IN")} against ₹${Number(p.billed).toLocaleString("en-IN")} billed. Could you please confirm settlement / share invoice at your convenience? Thanks 🙏`;
              return (
                <tr key={v.id} data-testid={`vendor-row-${v.id}`}>
                  <td><Link to={`/vendors/${v.id}`} className="font-medium hover:underline">{v.name}</Link></td>
                  <td className="text-xs text-[#75706A]">{v.contact || "—"}</td>
                  <td className="mono text-xs">{v.phone || "—"}</td>
                  <td className="text-xs text-[#75706A] max-w-xs truncate">{v.material_types || "—"}</td>
                  <td className="mono">{inr(p.billed)}</td>
                  <td className="mono text-[#4A7258]">{inr(p.paid)}</td>
                  <td className={`mono font-bold ${p.outstanding > 0 ? "text-[#B83A3A]" : p.outstanding < 0 ? "text-[#4A7258]" : ""}`}>{inr(p.outstanding)}</td>
                  <td>
                    <div className="flex flex-wrap gap-1.5">
                      {v.phone && p.outstanding > 0 && (
                        <WhatsAppButton phone={v.phone} message={remindMsg} label="Remind" size="sm" testid={`vendor-remind-${v.id}`}/>
                      )}
                      {v.phone && <WhatsAppShortcuts phone={v.phone} name={v.name} variant="compact" testid={`wa-vendor-${v.id}`}/>}
                      <Link to={`/vendors/${v.id}`} className="btn-ghost text-xs">Open →</Link>
                      <AdminDeleteButton endpoint={`/vendors/${v.id}`} label={`vendor "${v.name}"`} onDone={load} testid={`delete-vendor-${v.id}`}/>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const VLKpi = ({ label, value, tone }) => (
  <div className="p-3 rounded-md bg-[#FBF8F1] border border-[#EAE5DB]">
    <div className="label-tiny">{label}</div>
    <div className={`font-display text-xl font-bold mt-1 ${tone||""}`}>{value}</div>
  </div>
);

export function Products() {
  const [cats, setCats] = React.useState([]);
  useEffect(() => { api.get("/product-categories").then(r => setCats(r.data || [])); }, []);
  return <EntityPage testid="products" title="Products" subtitle="Catalog with category, HSN, default price, GST & 3XL+ upcharge"
    endpoint="/products"
    defaults={{ name: "", category: "", hsn_code: "", default_price: 0, default_gst: 18, oversize_surcharge: 0 }}
    columns={[
      { key: "name", label: "Product" },
      { key: "category", label: "Category", type: "select",
        options: ["", ...cats.map(c => c.name)],
        editable: true },
      { key: "hsn_code", label: "HSN" },
      { key: "default_price", label: "Default price ₹", type: "number", render: r => inr(r.default_price) },
      { key: "default_gst", label: "GST %", type: "number" },
      { key: "oversize_surcharge", label: "3XL+ upcharge ₹/pc", type: "number", render: r => r.oversize_surcharge ? inr(r.oversize_surcharge) : "—" },
    ]}
    extraFormFooter={<CategoryQuickAdd onAdded={c => setCats(cs => [c, ...cs])} />}
  />;
}

function CategoryQuickAdd({ onAdded }) {
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/product-categories", { name: name.trim() });
      onAdded(data);
      toast.success(`Category "${data.name}" added`);
      setName("");
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="md:col-span-2 pt-3 border-t border-[#EAE5DB] flex items-end gap-2">
      <div className="flex-1">
        <div className="label-tiny mb-1">+ New product category (optional)</div>
        <input className="input-field" placeholder='e.g. "Jersey" or "Kids Wear"' value={name} onChange={e => setName(e.target.value)} data-testid="product-cat-new" />
      </div>
      <button type="button" onClick={add} disabled={busy || !name.trim()} className="btn-ghost text-xs disabled:opacity-50" data-testid="product-cat-add">Add category</button>
    </div>
  );
}

export function Samples() {
  return <EntityPage testid="samples" title="Samples / DC" subtitle="5-day return timer; overdue samples flag automatically"
    endpoint="/samples"
    defaults={{ customer_name: "", product: "", qty: 1, dc_type: "refundable", amount_collected: 0, expected_return_at: "", notes: "" }}
    columns={[
      { key: "customer_name", label: "Customer" },
      { key: "product", label: "Product" },
      { key: "qty", label: "Qty", type: "number" },
      { key: "dc_type", label: "Type", type: "select", options: ["free","paid","refundable"] },
      { key: "amount_collected", label: "Deposit", type: "number", render: r => inr(r.amount_collected||0) },
      { key: "expected_return_at", label: "Return by", type: "date" },
      { key: "return_status", label: "Status", editable: false, render: r => {
          if (r.return_status === "returned") return <span className="badge badge-success">Returned</span>;
          if (r.overdue) return <span className="badge badge-danger">Overdue {r.days_outstanding}d</span>;
          return <span className="badge badge-warn">Pending</span>;
        }},
      { key: "notes", label: "Notes" },
    ]}
  />;
}

export function Complaints() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [popup, setPopup] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", subject: "", description: "", status: "open",
    order_id: "", reported_by: "", resolution: "", cost_impact: 0, severity: "medium",
  });

  const load = async () => {
    setItems((await api.get("/complaints")).data);
    setOrders((await api.get("/orders")).data);
  };
  useEffect(()=>{ load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/complaints", { ...form, order_id: form.order_id || null });
      setForm({ customer_name: "", subject: "", description: "", status: "open", order_id: "", reported_by: "", resolution: "", cost_impact: 0, severity: "medium" });
      setShowAdd(false); load(); toast.success("Complaint logged");
    } catch { toast.error("Failed"); }
  };

  const resolve = async (c) => {
    const resolution = window.prompt("Resolution / action taken?", c.resolution || "");
    if (resolution == null) return;
    const cost_impact = Number(window.prompt("Cost impact ₹ (if any)?", c.cost_impact || 0) || 0);
    try {
      await api.put(`/complaints/${c.id}`, { status: "resolved", resolution, cost_impact, resolved_at: new Date().toISOString() });
      toast.success("Complaint marked resolved"); load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">After delivery</div>
          <h1 className="font-display text-3xl tracking-tight">Complaints & Returns</h1>
          <p className="text-sm text-[#75706A] mt-1">Every complaint gets a resolution note, severity and cost impact.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={()=>setShowAdd(v=>!v)} data-testid="complaints-add-btn">
          {showAdd ? <><X size={16}/> Cancel</> : <><Plus size={16}/> New complaint</>}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submit} className="card-surface p-5 grid grid-cols-1 md:grid-cols-3 gap-4 fade-up" data-testid="complaint-form">
          <div><div className="label-tiny mb-1">Customer *</div>
            <input required className="input-field" value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})} data-testid="complaint-customer"/></div>
          <div><div className="label-tiny mb-1">Link to order (optional)</div>
            <select className="input-field" value={form.order_id} onChange={e=>setForm({...form,order_id:e.target.value})} data-testid="complaint-order">
              <option value="">— No order</option>
              {orders.map(o => <option key={o.id} value={o.id}>{o.order_no} · {o.customer_name}</option>)}
            </select></div>
          <div><div className="label-tiny mb-1">Severity</div>
            <select className="input-field" value={form.severity} onChange={e=>setForm({...form,severity:e.target.value})}>
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select></div>
          <div className="md:col-span-3"><div className="label-tiny mb-1">Subject *</div>
            <input required className="input-field" value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} data-testid="complaint-subject"/></div>
          <div className="md:col-span-3"><div className="label-tiny mb-1">Description</div>
            <textarea rows={3} className="input-field" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
          <div><div className="label-tiny mb-1">Reported by</div>
            <input className="input-field" value={form.reported_by} onChange={e=>setForm({...form,reported_by:e.target.value})} placeholder="Sales rep / staff name"/></div>
          <div><div className="label-tiny mb-1">Resolution notes</div>
            <input className="input-field" value={form.resolution} onChange={e=>setForm({...form,resolution:e.target.value})} placeholder="Action taken (optional)"/></div>
          <div><div className="label-tiny mb-1">Cost impact ₹</div>
            <input type="number" className="input-field" value={form.cost_impact} onChange={e=>setForm({...form,cost_impact:Number(e.target.value)})}/></div>
          <div className="md:col-span-3 flex justify-end"><button className="btn-primary" data-testid="complaint-submit">Log complaint</button></div>
        </form>
      )}

      <div className="card-surface overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>Date</th><th>Customer</th><th>Order</th><th>Subject</th><th>Severity</th><th>Status</th><th>Cost ₹</th><th>Actions</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#75706A]">No complaints logged.</td></tr>}
            {items.map(c => {
              const ord = orders.find(o=>o.id===c.order_id);
              return (
                <tr key={c.id} data-testid={`complaint-row-${c.id}`} className="cursor-pointer" onClick={()=>setPopup({...c, orderNo: ord?.order_no})}>
                  <td className="mono text-xs">{c.created_at?.slice(0,10)}</td>
                  <td>{c.customer_name}</td>
                  <td>{ord ? <Link to={`/orders/${ord.id}`} className="mono hover:underline" onClick={e=>e.stopPropagation()}>{ord.order_no}</Link> : "—"}</td>
                  <td className="max-w-xs truncate">{c.subject}</td>
                  <td>{c.severity && <span className={`badge ${c.severity==="high"?"badge-danger":c.severity==="low"?"badge-neutral":"badge-warn"}`}>{c.severity}</span>}</td>
                  <td><span className={`badge ${c.status==="resolved"?"badge-success":"badge-warn"}`}>{c.status}</span></td>
                  <td className="mono">{c.cost_impact ? inr(c.cost_impact) : "—"}</td>
                  <td onClick={e=>e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      {c.status !== "resolved" && <button className="btn-ghost text-xs" onClick={()=>resolve(c)} data-testid={`complaint-resolve-${c.id}`}>Resolve</button>}
                      <AdminDeleteButton endpoint={`/complaints/${c.id}`} label={`complaint "${c.subject}"`} onDone={load} testid={`delete-complaint-${c.id}`} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DetailPopup open={!!popup} onClose={()=>setPopup(null)}
        subtitle="Complaint" title={popup?.subject || ""}>
        {popup && <>
          <div><span className="text-[#75706A]">Customer:</span> <b>{popup.customer_name}</b>{popup.orderNo && <span> · <span className="mono">{popup.orderNo}</span></span>}</div>
          <div><span className="text-[#75706A]">Status:</span> <span className={`badge ${popup.status==="resolved"?"badge-success":"badge-warn"}`}>{popup.status}</span> · Severity <b>{popup.severity || "medium"}</b></div>
          {popup.reported_by && <div><span className="text-[#75706A]">Reported by:</span> {popup.reported_by}</div>}
          <div><span className="text-[#75706A]">Date:</span> <span className="mono">{popup.created_at?.slice(0,10)}</span></div>
          {popup.description && <div className="mt-2 pt-2 border-t border-[#E7E1D3]"><div className="label-tiny mb-1">Description</div>{popup.description}</div>}
          {popup.resolution && <div className="mt-2 pt-2 border-t border-[#E7E1D3]"><div className="label-tiny mb-1">Resolution</div>{popup.resolution}</div>}
          {popup.cost_impact ? <div className="mt-2"><span className="text-[#75706A]">Cost impact:</span> <span className="mono font-bold">{inr(popup.cost_impact)}</span></div> : null}
          {popup.resolved_at && <div className="text-xs text-[#75706A] mt-1">Resolved at {popup.resolved_at.slice(0,10)}</div>}
        </>}
      </DetailPopup>
    </div>
  );
}

/** Expenses page with order picker to link expense against a specific order. */
export function Expenses() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [popup, setPopup] = useState(null);
  const [form, setForm] = useState({ order_id: "", category: "material", description: "", amount: 0, vendor: "" });
  const [filter, setFilter] = useState("");

  const load = async () => {
    setItems((await api.get("/expenses")).data);
    setOrders((await api.get("/orders")).data);
  };
  useEffect(()=>{ load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, order_id: form.order_id || null });
      setForm({ order_id: "", category: "material", description: "", amount: 0, vendor: "" });
      setShowAdd(false); load(); toast.success("Expense recorded");
    } catch(err){ toast.error("Failed"); }
  };

  const shown = filter ? items.filter(e => e.order_id === filter) : items;
  const total = shown.reduce((a,b)=>a+Number(b.amount||0),0);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Real spending</div>
          <h1 className="font-display text-3xl tracking-tight">Expenses</h1>
          <p className="text-sm text-[#757169] mt-1">Every rupee spent. Link to an order to see order-level profit.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={()=>setShowAdd(v=>!v)}>
          {showAdd ? <><X size={16}/> Cancel</> : <><Plus size={16}/> New expense</>}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={submit} className="card-surface p-5 grid grid-cols-1 md:grid-cols-3 gap-4 fade-up">
          <div className="md:col-span-2">
            <div className="label-tiny mb-1">Link to order (optional)</div>
            <select className="input-field" value={form.order_id} onChange={e=>setForm({...form,order_id:e.target.value})} data-testid="expense-order">
              <option value="">— No order (general/overhead)</option>
              {orders.map(o => <option key={o.id} value={o.id}>{o.order_no} · {o.customer_name} · {inr(o.total)}</option>)}
            </select>
          </div>
          <div>
            <div className="label-tiny mb-1">Category</div>
            <select className="input-field" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
              <option value="material">Material</option><option value="customization">Customization</option>
              <option value="transport">Transport</option><option value="packaging">Packaging</option><option value="other">Other</option>
            </select>
          </div>
          <div className="md:col-span-2"><div className="label-tiny mb-1">Description</div><input className="input-field" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
          <div><div className="label-tiny mb-1">Amount</div><input required type="number" step="0.01" className="input-field" value={form.amount} onChange={e=>setForm({...form,amount:Number(e.target.value)})}/></div>
          <div><div className="label-tiny mb-1">Paid to</div><input className="input-field" value={form.vendor} onChange={e=>setForm({...form,vendor:e.target.value})}/></div>
          <div className="md:col-span-3 flex justify-end"><button className="btn-primary">Record</button></div>
        </form>
      )}

      <div className="flex items-center gap-3">
        <span className="label-tiny">Filter by order:</span>
        <select className="input-field max-w-md" value={filter} onChange={e=>setFilter(e.target.value)}>
          <option value="">All expenses</option>
          {orders.map(o => <option key={o.id} value={o.id}>{o.order_no} · {o.customer_name}</option>)}
        </select>
        <span className="ml-auto text-sm text-[#757169]">Total: <span className="mono font-bold text-[#2C2B29]">{inr(total)}</span></span>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>Date</th><th>Order</th><th>Category</th><th>Description</th><th>Paid to</th><th>Amount</th><th></th></tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#757169]">No expenses.</td></tr>}
            {shown.map(e => {
              const ord = orders.find(o=>o.id===e.order_id);
              return (
                <tr key={e.id} className="cursor-pointer" onClick={()=>setPopup({...e, order_no: ord?.order_no, customer_name: ord?.customer_name})}>
                  <td className="mono text-xs">{e.created_at?.slice(0,10)}</td>
                  <td>{ord ? <Link to={`/orders/${ord.id}`} className="mono hover:underline" onClick={ev=>ev.stopPropagation()}>{ord.order_no}</Link> : <span className="text-[#757169] text-xs">— general</span>}</td>
                  <td>{e.category}</td>
                  <td>{e.description || "—"}</td>
                  <td>{e.vendor || "—"}</td>
                  <td className="mono">{inr(e.amount)}</td>
                  <td onClick={ev=>ev.stopPropagation()}>
                    <AdminDeleteButton endpoint={`/expenses/${e.id}`} label={`expense ${inr(e.amount)}`} onDone={load} testid={`delete-expense-${e.id}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DetailPopup open={!!popup} onClose={()=>setPopup(null)} subtitle="Expense" title={popup ? inr(popup.amount) : ""}>
        {popup && <>
          <div><span className="text-[#75706A]">Category:</span> {popup.category}</div>
          <div><span className="text-[#75706A]">Paid to:</span> {popup.vendor || "—"}</div>
          <div><span className="text-[#75706A]">Description:</span> {popup.description || "—"}</div>
          {popup.order_no && <div><span className="text-[#75706A]">Order:</span> <span className="mono">{popup.order_no}</span> · {popup.customer_name}</div>}
          <div><span className="text-[#75706A]">Recorded:</span> <span className="mono">{popup.created_at?.slice(0,10)}</span></div>
        </>}
      </DetailPopup>
    </div>
  );
}
